import { isAppInWebView } from 'helpers/Device';

import thor from './Thor';
import { referrerOrigin, getSearchParamsAsObject } from './Referrer';
import { isBarometreStepNumber } from './FormInformations';

const AV_CATEGORY = 'estimation_funnel';
const AV_USER = 'av_user';
const APARTMENT_TYPE = ['apartment', 'duplex', 'triplex', 'loft'];

export const PAGE_VIEW_EVENT_NAME = 'page_view';
export const INTERACTION_EVENTS_LIST = {
    submitContactFormAttempt: 'lead_estfunnel_form_submit',
    submitContactFormSuccess: 'lead_estfunnel_form_confirm',
    submitEstimationSuccess: 'estimation_funnel_confirmation',
};

const PAGE_TITLES = {
    address: 'address',
    'address-suggest': 'address_suggest',
    type: 'estateType',
    facts: 'characteristics',
    features: 'benefits',
    more: 'conditions',
    project: 'profile',
    login: 'contact_details',
    retrievePhone: 'retrieve_phone',
    'fake-animation': 'calculation',
};

const GA4_ESTATE_TYPE_GLOBAL = {
    appartment: 'av_2',
    house: 'av_5',
};

const FUNNEL_TYPE = {
    first: 'first', // when an account is created in the funnel
    new: 'new', // all the estimation journey from step 1
    update: 'update', // update of estimation and not all the steps are fullfilled
};

const USER_STATUS = {
    logged: 'logged',
    unlogged: 'unlogged',
};

const PRO_JOB_DICT = {
    'USER_PROFILE.REALTOR': 'Realtor',
    'USER_PROFILE.REPRESENTATIVE': 'ProxyAgent',
    'USER_PROFILE.SYNDIC': 'Syndic',
    'USER_PROFILE.INSURER': 'Insurer',
    'USER_PROFILE.BANKER': 'Banker',
    'USER_PROFILE.ASSET_ADVISOR': 'AssetAdvisor',
    'USER_PROFILE.BROKER': 'Broker',
    'USER_PROFILE.DIAGNOSER': 'Diagnoser',
    'USER_PROFILE.REAL_ESTATE_EXPERT': 'RealEstateExpert',
    'USER_PROFILE.INSTITUTIONNAL': 'Institutional',
    'USER_PROFILE.NOTARY': 'Notary',
    'USER_PROFILE.ESTATE_DEVELOPER': 'Developer',
    'USER_PROFILE.OTHER': 'Other',
};

// Some tracking values of seller profile (`WantsToSellInLessThanOneYear` and `WantsToSellInMoreThanOneYear`) don't exist in Meilleurs Agents model.
// The seller profile `sold` doesn't not exist in the tracking plan. This level is replaced by `StartedSelling`
const PROFILE_SELLER = {
    'SELLER_PROFILE.STARTED_SELLING': 'StartedSelling',
    'SELLER_PROFILE.SELL_ASAP': 'WantsToSellAsap',
    'SELLER_PROFILE.SELL_IN_THREE_MONTHS': 'WantsToSellInLessThanThreeMonths',
    'SELLER_PROFILE.SELL_IN_SIX_MONTHS': 'WantsToSellInLessThanSixMonths',
    'SELLER_PROFILE.SELL_IN_MORE_THAN_SIX_MONTHS': 'WantsToSellInLessThanOneYear',
    'SELLER_PROFILE.SOLD': 'StartedSelling',
    'SELLER_PROFILE.NO_SALE_PROJECT': 'NoSaleProject',
};

const PROFILE_BUYER = {
    'BUYER_PROFILE.BUY': 'WantsToBuy',
    'BUYER_PROFILE.SELL': 'Sold',
    BUYER_PROFILE_PROSPECTIVE_TENANT: 'WantsToRent',
    BUYER_PROFILE_EXISTING_TENANT: 'IsCurrentTenant',
    'BUYER_PROFILE.TOURIST': 'Curious',
};

const formatValue = (string) =>
    string
        .replace(/[-\s]/g, '_') // space | dash to be replaced by underscore
        .normalize('NFD') // normalize
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase(); // in lowercase

const setFunnelType = () => {
    if (window.location.pathname.includes('update')) {
        return FUNNEL_TYPE.update;
    }

    return window.analyticsUser?.authenticated ? FUNNEL_TYPE.new : FUNNEL_TYPE.first;
};

const setPageTitle = (step) => {
    const stepNumber = step.number - 1;
    const pageName = PAGE_TITLES[step.id] ?? `unknown_page_${step.id}`;
    const funnelType = setFunnelType();

    if (
        window.location.pathname.includes('update') &&
        window.location.search.includes('estima_id')
    ) {
        return 'estimate_update';
    }

    return `estimate_step${stepNumber}_${pageName}_${funnelType}`;
};

const setEstateType = (propertyType) =>
    APARTMENT_TYPE.includes(propertyType)
        ? GA4_ESTATE_TYPE_GLOBAL.appartment
        : GA4_ESTATE_TYPE_GLOBAL.house;

export const setUserProject = (formValues) => {
    if (formValues?.user_is_pro) return `Pro_${PRO_JOB_DICT[formValues.user_profile]}`;

    if (formValues?.profile_owner && formValues?.profile_seller) {
        return `Owner_${PROFILE_SELLER[formValues.profile_seller]}`;
    }
    if (!formValues?.profile_owner && formValues?.profile_buyer) {
        return `NonOwner_${PROFILE_BUYER[formValues.profile_buyer]}`;
    }
};

const setUser = (formValues) => {
    const project = setUserProject(formValues);
    const newValues = {
        ...(window.analyticsUser && {
            status: window.analyticsUser.authenticated ? USER_STATUS.logged : USER_STATUS.unlogged,
            ...(window.analyticsUser.id && { uid: window.analyticsUser.id }),
        }),
        ...(project && { project }),
        ...(window.realtor && { intermediary: window.realtor.position }),
    };

    const currentValues = thor.getDataLayerValue(AV_USER);

    if (currentValues !== '') return { ...currentValues, ...newValues };

    return newValues;
};

const sharedData = {
    av_url_path: window.location.pathname,
    av_category: AV_CATEGORY,
    av_language: 'fr',
    av_environment: window.MA.Config.ENVIRONMENT,
    av_platform: isAppInWebView() ? 'app' : 'web',
    av_site: 'MA',
    av_medium: referrerOrigin(getSearchParamsAsObject()),
};

export const composePageViewPayload = ({ steps, form }) => ({
    av_pagetitle: setPageTitle(steps),
    [AV_USER]: setUser(form?.values),
    ...(form?.values?._property_type && {
        av_estate_type_global: setEstateType(form.values._property_type),
    }),
    ...(form?.values?.city_name && { av_city: formatValue(form.values.city_name) }),
    ...(form?.values?.zip && { av_zip_code: form.values.zip }),
    ...(isBarometreStepNumber(steps.number) && {
        av_user: {
            project: window.estima.profile.seller_profile
                ? `Owner_${PROFILE_SELLER[window.estima.profile.seller_profile.key]}`
                : `NonOwner_${PROFILE_BUYER[window.estima.profile.buyer_profile?.key]}`,
        },
        av_distribution_type_global: '2',
        av_estate_type_global: setEstateType(window.estima.item_type.name),
        av_estimation_value: window.estima.price,
        av_category: 'estimate',
    }),
});

export const composeInteractionPayload = ({ form }) => ({
    method: setFunnelType(),
    [AV_USER]: setUser(form.values),
    ...(form.values?._property_type && {
        av_estate_type_global: setEstateType(form.values?._property_type),
    }),
    ...(form.values?.city_name && { av_city: form.values.city_name }),
    ...(form.values?.zip && { av_zip_code: form.values.zip }),
});

const composeBasePayload = (form) => ({
    av_distribution_type_global: '2', //
    av_estate_type_global: setEstateType(form.values?._property_type || form._property_type),
    'av_user.project': setUserProject(form),
    av_medium: referrerOrigin(getSearchParamsAsObject()),
    av_category: AV_CATEGORY,
});

const composeSubmitContactPayload = ({ form }) => ({
    ...composeBasePayload(form),
});

const composeSubmitPayload = ({ form }) => ({
    ...composeBasePayload(form),
    av_city: formatValue(form.city_name),
    av_zip_code: form.zip,
});

export const thorPushEvent = (event) => {
    const { eventType, payload } = event;

    switch (eventType) {
        case PAGE_VIEW_EVENT_NAME:
            thor.pushEvent(eventType, {
                ...sharedData,
                ...composePageViewPayload(payload),
            });
            break;

        case INTERACTION_EVENTS_LIST.submitContactFormAttempt:
        case INTERACTION_EVENTS_LIST.submitContactFormSuccess:
            thor.pushEvent(eventType, composeSubmitContactPayload(payload));
            break;

        case INTERACTION_EVENTS_LIST.submitEstimationSuccess:
            thor.pushEvent(eventType, composeSubmitPayload(payload));
            break;

        default:
            thor.pushEvent(eventType, { ...sharedData, ...composeInteractionPayload(payload) });
    }
};
